<script setup>
/**
 * Connected AI apps (services spec §8 P-8.4): who connected which app, when, when it was last
 * used, and Disconnect, which stops its very next request. Below it, "Use Waslo from your Claude":
 * the one address she adds in Claude. There is no key to copy, ever.
 */
import { computed, onMounted, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import { useWasloI18n } from '../i18n.js';
import { relativeTime } from '../helpers/relative-time.js';
import { connectorUrl } from '../helpers/claude.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
});

const { t, locale } = useWasloI18n();

const grants = ref(null);
const failed = ref(false);
const busy = ref(false);
const notice = ref('');
const copied = ref(false);

const url = computed(() =>
  connectorUrl(typeof window === 'undefined' ? '' : window.location.origin)
);

const load = async () => {
  if (props.accountId === null) return;
  failed.value = false;
  try {
    grants.value = await wasloApi.getAssistantGrants(props.accountId);
  } catch {
    failed.value = true;
  }
};

onMounted(load);

const when = iso =>
  relativeTime(iso, locale.value, {
    justNow: t('WASLO.CLAUDE.APPS.JUST_NOW'),
  }) ?? '';

const disconnect = async grant => {
  if (busy.value || props.accountId === null) return;
  busy.value = true;
  notice.value = '';
  try {
    await wasloApi.revokeAssistantGrant(props.accountId, grant.id);
    grants.value = (grants.value ?? []).filter(g => g.id !== grant.id);
    notice.value = t('WASLO.CLAUDE.APPS.DISCONNECTED');
  } catch {
    notice.value = t('WASLO.CLAUDE.APPS.FAILED');
  } finally {
    busy.value = false;
  }
};

const copy = async () => {
  try {
    await navigator.clipboard.writeText(url.value);
    copied.value = true;
  } catch {
    copied.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col gap-4" data-waslo-connected-apps>
    <SettingsHeader
      :heading="t('WASLO.CLAUDE.APPS.HEADING')"
      :description="t('WASLO.CLAUDE.APPS.DESCRIPTION')"
    />

    <p v-if="failed" class="mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.CLAUDE.APPS.UNAVAILABLE') }}
    </p>
    <ul
      v-else-if="grants && grants.length"
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
    >
      <li
        v-for="grant in grants"
        :key="grant.id"
        class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
        :data-waslo-grant="grant.id"
      >
        <div class="flex flex-col min-w-0 gap-1 grow">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            <bdi>{{ grant.clientName }}</bdi>
          </p>
          <p class="mb-0 text-xs text-n-slate-10" dir="auto">
            {{
              t('WASLO.CLAUDE.APPS.CONNECTED_BY', {
                who: grant.connectedBy,
                when: when(grant.connectedAt),
              })
            }}
          </p>
          <p class="mb-0 text-xs text-n-slate-10">
            {{
              grant.lastUsedAt
                ? t('WASLO.CLAUDE.APPS.LAST_USED', {
                    when: when(grant.lastUsedAt),
                  })
                : t('WASLO.CLAUDE.APPS.NEVER_USED')
            }}
          </p>
        </div>
        <Button
          :label="t('WASLO.CLAUDE.APPS.DISCONNECT')"
          variant="faded"
          color="ruby"
          size="sm"
          :disabled="busy"
          data-waslo-grant-disconnect
          @click="disconnect(grant)"
        />
      </li>
    </ul>
    <p v-else-if="grants" class="mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.CLAUDE.APPS.NONE') }}
    </p>
    <p
      v-if="notice"
      class="mb-0 text-sm text-n-slate-12"
      data-waslo-grants-notice
    >
      {{ notice }}
    </p>

    <div
      class="flex flex-col gap-2 p-4 rounded-xl outline outline-1 outline-n-weak"
      data-waslo-connector-how
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.CLAUDE.APPS.HOW_HEADING') }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.CLAUDE.APPS.HOW_ADD') }}
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <code
          class="px-2 py-1 text-sm rounded-md bg-n-alpha-2 text-n-slate-12"
          dir="ltr"
          data-waslo-connector-url
          >{{ url }}</code
        >
        <Button
          :label="
            copied ? t('WASLO.CLAUDE.APPS.COPIED') : t('WASLO.CLAUDE.APPS.COPY')
          "
          variant="faded"
          color="slate"
          size="sm"
          data-waslo-connector-copy
          @click="copy"
        />
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('WASLO.CLAUDE.APPS.HOW_ALLOW') }}
      </p>
    </div>
  </section>
</template>
